// A network moves through two sunset stages:
//   "sunsetting" — sunset announced, dApp live, unstake window open.
//                  Cards show "Unstake Now" + "Learn more"; links point at the dApp.
//   "sunset"     — window closed, dApp retired. Cards show "Learn more" only and every
//                  link is rewritten to learnMoreUrl, because the dApp URLs now 404.
export type SunsetStatus = "sunsetting" | "sunset";

// Same yellow as the sunset banner, so "discontinued" reads consistently across
// the site rather than borrowing the accent colour used for promo tags.
export const DISCONTINUED_BADGE_BG = "#FBC828";
export const DISCONTINUED_BADGE_COLOR = "#142528";

export interface DiscontinuedNetwork {
  status: SunsetStatus;
  badge: string;
  // Only set for "sunsetting" networks — a retired dApp has nothing to unstake from.
  primaryCTAText?: string;
  primaryCTAUrl?: string;
  learnMoreText: string;
  learnMoreUrl: string;
  description: string; // docs-card subtext override
  // Set when the CMS has already dropped this network from the nav data. The entry
  // is re-injected by `prepareNavData` so the network keeps its badge and Learn
  // more link on the header, homepage cards and footer.
  navFallback?: {
    id: number;
    detailId: string;
    label: string;
    imgKey: string;
  };
}

export const DISCONTINUED_NETWORKS: Record<string, DiscontinuedNetwork> = {
  polygon: {
    status: "sunset",
    badge: "Discontinued",
    learnMoreText: "Learn more",
    learnMoreUrl: "https://stader.gitbook.io/stader/polygon-discontinued",
    description: "MaticX has been discontinued",
  },
  bnb: {
    status: "sunset",
    badge: "Discontinued",
    learnMoreText: "Learn more",
    learnMoreUrl: "https://stader.gitbook.io/stader/bnb-chain-deprecated",
    description: "BNBx has been discontinued",
    navFallback: {
      id: -1,
      detailId: "bnb",
      label: "BNB Chain",
      imgKey: "bnb",
    },
  },
};

export const getDiscontinued = (
  key?: string
): DiscontinuedNetwork | undefined =>
  key ? DISCONTINUED_NETWORKS[key.toLowerCase()] : undefined;

export const isDiscontinued = (key?: string): boolean =>
  Boolean(getDiscontinued(key));

// Any nav/card link for a fully sunset network has to go to Learn more — the dApp
// it used to point at no longer exists.
export const resolveDiscontinuedLink = <T extends string | null | undefined>(
  key: string | undefined,
  link: T
): T | string => {
  const network = getDiscontinued(key);
  return network?.status === "sunset" ? network.learnMoreUrl : link;
};
